'use client'

// The rave's speakers.
//
// The sim has always known when music is playing (the ambient cycle, a
// booked gig, a band the holders voted in) and nothing in the world
// showed it: the stacks stood still through every set. Now the two
// cabinets thump on the beat while musicPlaying is on and sit still when
// it is off. Each cabinet is scaled about its own centre, because the
// model's geometry is baked in scene space and scaling the mesh itself
// would slide it across the square.

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import { useModel } from '../hooks/useModel'
import { useNodeMaterials } from '../hooks/useNodeMaterials'
import useGameStore from '../../../stores/gameStore'

const BPM = 118
const KICK = 0.045   // how far a cabinet swells on the kick

function Cabinet({ node, material }) {
  const group = useRef(null)
  const center = useMemo(() => {
    const g = node.geometry
    if (!g.boundingBox) g.computeBoundingBox()
    const c = new THREE.Vector3()
    g.boundingBox.getCenter(c)
    return c
  }, [node])
  const level = useRef(0)
  useFrame(({ clock }, dt) => {
    const g = group.current
    if (!g) return
    const on = !!useGameStore.getState().environment?.musicPlaying
    // Eased in and out, so the stacks come up with the music rather
    // than snapping into a pulse.
    level.current += ((on ? 1 : 0) - level.current) * Math.min(1, dt * 2)
    const phase = (clock.elapsedTime * BPM) / 60
    const kick = Math.pow(Math.max(0, Math.cos(phase * Math.PI * 2)), 10)
    g.scale.setScalar(1 + kick * KICK * level.current)
  })
  return (
    <group ref={group} position={center}>
      <mesh geometry={node.geometry} material={material} position={[-center.x, -center.y, -center.z]} castShadow receiveShadow />
    </group>
  )
}

export function Speakers(props) {
  const { nodes, materials } = useModel('/models/speakers.glb')
  const { getMat } = useNodeMaterials(materials)

  return (
    <group {...props} dispose={null}>
      <Cabinet node={nodes.Mesh_Venue_Speaker_09} material={getMat(nodes.Mesh_Venue_Speaker_09)} />
      <mesh geometry={nodes.Mesh_TrussBeam_01.geometry} material={getMat(nodes.Mesh_TrussBeam_01)} castShadow receiveShadow />
      <mesh geometry={nodes.Mesh_TrussBeam_01001.geometry} material={getMat(nodes.Mesh_TrussBeam_01001)} castShadow receiveShadow />
      <Cabinet node={nodes.Mesh_Venue_Speaker_09001} material={getMat(nodes.Mesh_Venue_Speaker_09001)} />
      <mesh geometry={nodes.Mesh_TrussBeam_01002.geometry} material={getMat(nodes.Mesh_TrussBeam_01002)} castShadow receiveShadow />
      <mesh geometry={nodes.Mesh_TrussBeam_01003.geometry} material={getMat(nodes.Mesh_TrussBeam_01003)} castShadow receiveShadow />
    </group>
  )
}
